/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CheckCircle2, Plus } from 'lucide-react';
import { type ChangeEvent, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type SessionRequestStatus } from '../../lib/api/session-requests.js';
import { type Session } from '../../lib/api/sessions.js';
import { useClients } from '../../lib/queries/clinical.js';
import {
  useCreateSessionRequest,
  useMySessionRequests,
} from '../../lib/queries/session-requests.js';
import { useSessions } from '../../lib/queries/sessions.js';
import { formatIst } from '../../lib/utils/datetime.js';

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';
const ACTIVE = new Set(['scheduled', 'in_progress']);
const STATUS_TONE: Readonly<Record<SessionRequestStatus, BadgeTone>> = {
  pending: 'amber',
  approved: 'green',
  declined: 'rose',
};

/** "Request Session" modal (Figma): Name · Preferred Date · Preferred Time · Phone. */
function RequestModal({
  open,
  onClose,
  childId,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly childId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateSessionRequest();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [phone, setPhone] = useState('');

  const onDate = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  }, []);
  const onTime = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTime(e.target.value);
  }, []);
  const onPhone = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
  }, []);
  const onModal = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose],
  );
  const submit = useCallback(() => {
    if (childId === '') {
      return;
    }
    create.mutate(
      {
        clientId: childId,
        ...(date !== '' ? { preferredDate: date } : {}),
        ...(time !== '' ? { preferredTime: time } : {}),
        ...(phone !== '' ? { notes: t('book.phoneNote', { phone }) } : {}),
      },
      {
        onSuccess: () => {
          setDate('');
          setTime('');
          setPhone('');
          onClose();
        },
      },
    );
  }, [create, childId, date, time, phone, onClose, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onModal}
      title={t('book.requestTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="rq-date" className={LABEL}>
            {t('book.preferredDate')}
          </label>
          <input id="rq-date" type="date" className={INPUT} value={date} onChange={onDate} />
        </div>
        <div>
          <label htmlFor="rq-time" className={LABEL}>
            {t('book.preferredTime')}
          </label>
          <input
            id="rq-time"
            className={INPUT}
            placeholder={t('book.timePlaceholder')}
            value={time}
            onChange={onTime}
          />
        </div>
        <div>
          <label htmlFor="rq-phone" className={LABEL}>
            {t('book.phone')}
          </label>
          <input
            id="rq-phone"
            className={INPUT}
            placeholder="+91…"
            value={phone}
            onChange={onPhone}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={create.isPending}>
            {t('book.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** "Current Schedule" card — the child's upcoming recurring sessions. */
function CurrentSchedule({ sessions }: { readonly sessions: readonly Session[] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <h3 className="mb-3 font-semibold text-slate-800 dark:text-white">
        {t('book.currentSchedule')}
      </h3>
      {sessions.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">{t('book.noSchedule')}</p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center gap-3 py-3">
              <span className="w-12 shrink-0 text-sm font-semibold text-slate-500">
                {formatIst(s.scheduledStart, 'EEE')}
              </span>
              <span className="min-w-0 flex-1 text-sm text-slate-700 dark:text-slate-200">
                {formatIst(s.scheduledStart, 'h:mm a')} — {s.sessionType ?? t('book.session')}
              </span>
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" aria-hidden="true" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Parent → Book Appointment (Figma). Current schedule + Request Session. */
export function ParentBookPage(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const sessions = useSessions();
  const mine = useMySessionRequests();
  const [open, setOpen] = useState(false);
  const [childId, setChildId] = useState('');

  const children = useMemo(() => clients.data ?? [], [clients.data]);
  useEffect(() => {
    if (childId === '' && children.length > 0) {
      setChildId(children[0]?.id ?? '');
    }
  }, [children, childId]);

  const upcoming = useMemo(
    () =>
      (sessions.data ?? [])
        .filter((s) => s.clientId === childId && ACTIVE.has(s.status))
        .slice()
        .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart)),
    [sessions.data, childId],
  );
  const requests = mine.data ?? [];
  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div className="space-y-6 p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t('book.title')} subtitle={t('book.subtitle')} />
        <Button type="button" onClick={openModal}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('book.requestTitle')}
        </Button>
      </div>

      <CurrentSchedule sessions={upcoming} />

      {requests.length > 0 && (
        <div>
          <h3 className="mb-3 font-semibold text-slate-800 dark:text-white">
            {t('book.myRequests')}
          </h3>
          <QueryState
            isLoading={mine.isLoading}
            isError={mine.isError}
            isEmpty={requests.length === 0}
            loadingLabel={t('common.loading')}
            errorLabel={t('common.loadError')}
            emptyLabel={t('book.empty')}
          >
            <div className="space-y-2">
              {requests.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 dark:text-white">
                      {r.preferredDate !== null
                        ? formatIst(`${r.preferredDate}T00:00:00.000Z`, 'dd MMM yyyy')
                        : t('book.noPreference')}
                      {r.preferredTime !== null ? ` · ${r.preferredTime}` : ''}
                    </p>
                  </div>
                  <Badge label={t(`book.statuses.${r.status}`)} tone={STATUS_TONE[r.status]} />
                </div>
              ))}
            </div>
          </QueryState>
        </div>
      )}

      <RequestModal open={open} onClose={closeModal} childId={childId} />
    </div>
  );
}
